import { ProjectStatesDef } from '@project/components/project/constants/project-states-def';
import { UserVO } from '@user/model/vo/user.vo';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { ProjectOrderingUtils } from '@project/components/project/util/project-ordering-utils';

export class UserUtils {
  public static hasEditProjectPermission(user: UserVO): boolean {
    return user && user.hasPermission("EDIT_PROJECT");
  }

  public static hasEditProjectRemarkPermission(user: UserVO): boolean {
    return user && user.hasPermission("EDIT_PROJECT_REMARK");
  }

  public static hasReadSharedInfoFromAllUsersPermission(user: UserVO): boolean {
    return user && user.hasPermission("READ_SHARED_INFO_FROM_ALL_USERS");
  }

  public static hasEditArticleRemarkPermission(user: UserVO): boolean {
    return user && user.hasPermission("EDIT_ARTICLE_REMARK");
  }

  public static hasViewProjectRemarkPermission(user: UserVO): boolean {
    return user && user.hasPermission("VIEW_PROJECT_REMARK");
  }

  public static hasViewArticleRemarkPermission(user: UserVO): boolean {
    return user && user.hasPermission("VIEW_ARTICLE_REMARK");
  }

  public static hasGetListOfCustomArticleLibrariesPermission(user: UserVO): boolean {
    return user && user.hasPermission("GET_LIST_OF_CUSTOM_ARTICLE_LIBRARIES");
  }

  public static hasSaveProjectWithSubprojectsPermission(user: UserVO): boolean {
    return user && user.hasPermission("SAVE_PROJECT_WITH_SUBPROJECTS");
  }

  public static hasViewFavoriteArticlesPermission(user: UserVO): boolean {
    return user && user.hasPermission("VIEW_FAVORITE_ARTICLES");
  }

  public static hasEditFavoriteArticlesPermission(user: UserVO): boolean {
    return user && user.hasPermission("EDIT_FAVORITE_ARTICLES");
  }

  public static hasCaeImportPermission(user: UserVO): boolean {
    return user && user.hasPermission("IMPORT_FROM_CAE");
  }

  public static hasUseDrillingHoleViewPermission(user: UserVO): boolean {
    return user && user.hasPermission("USE_DRILLING_HOLE_VIEW");
  }

  public static hasPermissionToSaveProject(user: UserVO, project: ProjectVO): boolean {
    return (
      user &&
      project &&
      user.hasPermission("SAVE_PROJECTS") &&
      !project.isSharedReadOnly &&
      (!ProjectOrderingUtils.isWriteProtected(project, user) || ProjectOrderingUtils.canCreateNewVersion(project, user))
    );
  }

  public static hasPermissionToRenameProject(user: UserVO, project: ProjectHeadVO): boolean {
    return user && project && user.hasPermission("RENAME_PROJECTS") && !project.isSharedReadOnly;
  }

  public static hasPermissionToDeleteProject(user: UserVO, projectHead: ProjectHeadVO): boolean {
    return !!(
      user &&
      projectHead &&
      !projectHead.isSharedReadOnly &&
      projectHead.isLatestVersion &&
      (
        projectHead.finalizationState === undefined ||
        projectHead.finalizationState === null ||
        (projectHead.finalizationState < ProjectStatesDef.FINALIZATION_STATE_REQUESTED && projectHead.documentVersion < 0) ||
        user.hasPermission("DELETE_FINALIZED_PROJECTS")
      )
    );
  }

  public static hasPermissionToEditAllLabelings(user: UserVO): boolean {
    if (!user) {
      return false;
    }
    return user.hasPermission("EDIT_NON_DEFAULT_LABELING");
  }

  public static hasPermissionToEditTextureImageUsages(user: UserVO): boolean {
    return user && user.hasPermission("EDIT_TEXTURE_IMAGE_USAGES");
  }

  public static hasCreateMcsGdsProjectsPermission(user: UserVO): boolean {
    return user && user.hasPermission("CREATE_MCS_GDS_PROJECTS");
  }

  public static hasConvertToMcsGdsProjectPermission(user: UserVO): boolean {
    return user && user.hasPermission("CONVERT_TO_MCS_GDS_PROJECT");
  }

  public static hasConvertXcomArticlesToProjectPermission(user: UserVO): boolean {
    return user && user.hasPermission("CONVERT_XCOM_ARTICLES_TO_PROJECT");
  }

  public static isAdminOrClerk(user: UserVO): boolean {
    if (!user) {
      return false;
    }
    return user.isAdmin() || user.isClerk();
  }

  public static isWagoUser(user: UserVO): boolean {
    if (!user) {
      return false;
    }
    return UserUtils.isAdminOrClerk(user)
      || user.isProduction()
      || user.isSales();
  }

  public static hasAccessPriceCalculationPermission(user: UserVO): boolean {
    return user && user.hasPermission("ACCESS_PRICE_CALCULATION");
  }

  public static canSeePriceCalculationIcon(user: UserVO): boolean {
    if (!user) {
      return false;
    }
    return (
      user.isCustomer() ||
      user.hasPermission("ACCESS_PRICE_CALCULATION")
    );
  }

  public static canSeeExternalComments(user: UserVO, isInternalCommentEmpty: boolean): boolean {
    return user && (isInternalCommentEmpty || !UserUtils.canSeeInternalComments(user) || !user.isProduction());
  }

  public static canSeeInternalComments(user: UserVO): boolean {
    return this.isWagoUser(user);
  }

  public static hasUseOnlinePriceCalculationPermission(user: UserVO): boolean {
    return user && user.hasPermission("USE_ONLINE_PRICE_CALCULATION");
  }

  public static hasIncludeProjectsFromAllUsersPermission(user: UserVO): boolean {
    return user && user.hasPermission("VIEW_PROJECTS_FROM_ALL_USERS");
  }

  public static canBeFriend(user: UserVO): boolean {
    return user && (user.isCustomer() || user.isSales());
  }

  public static canHaveFriend(user: UserVO): boolean {
    return user && (user.isCustomer() || user.isSales() || user.isClerk());
  }

  public static hasFinalizeProjectsPermission(user: UserVO): boolean {
    return user && user.hasPermission("FINALIZE_PROJECTS");
  }

  public static hasPermissionToChangeMdmGroupForPdmExport(user: UserVO): boolean {
    return user && (user.isAdmin() || user.hasPermission("CHANGE_MDM_GROUP_FOR_PDM_EXPORT"));
  }
}
